/**
 * FormField Component
 * Renders individual form fields based on type
 * Supports all field types defined in the SecureForms schema
 */

import React from 'react';
import PropTypes from 'prop-types';
import styles from './FormField.module.css';

const FormField = ({ field, value, error, onChange, onBlur, disabled }) => {
    const {
        name,
        type,
        label,
        placeholder,
        required,
        options,
        rows,
        helpText,
        accept // for file inputs
    } = field;

    const handleChange = (e) => {
        const { type: inputType, checked, value: inputValue, files } = e.target;
        
        if (inputType === 'checkbox') {
            onChange(name, checked);
        } else if (inputType === 'file') {
            onChange(name, files);
        } else {
            onChange(name, inputValue);
        }
    };

    const handleBlur = () => {
        if (onBlur) {
            onBlur(name);
        }
    };

    const commonProps = {
        id: name,
        name,
        disabled,
        required,
        onBlur: handleBlur
    };

    /**
     * Render input based on field type
     */
    const renderInput = () => {
        switch (type) {
            case 'text':
            case 'email':
            case 'tel':
            case 'url':
            case 'password':
                return (
                    <input
                        {...commonProps}
                        type={type}
                        value={value || ''}
                        onChange={handleChange}
                        placeholder={placeholder}
                        className={`${styles.input} ${error ? styles.inputError : ''}`}
                    />
                );

            case 'number':
                return (
                    <input
                        {...commonProps}
                        type="number"
                        value={value || ''}
                        onChange={handleChange}
                        placeholder={placeholder}
                        min={field.validation?.min}
                        max={field.validation?.max}
                        step={field.validation?.step || 'any'}
                        className={`${styles.input} ${error ? styles.inputError : ''}`}
                    />
                );

            case 'date':
            case 'datetime-local':
            case 'time':
                return (
                    <input
                        {...commonProps}
                        type={type}
                        value={value || ''}
                        onChange={handleChange}
                        className={`${styles.input} ${error ? styles.inputError : ''}`}
                    />
                );

            case 'textarea':
                return (
                    <textarea
                        {...commonProps}
                        value={value || ''}
                        onChange={handleChange}
                        placeholder={placeholder}
                        rows={rows || 4}
                        className={`${styles.textarea} ${error ? styles.inputError : ''}`}
                    />
                );

            case 'select':
                return (
                    <select
                        {...commonProps}
                        value={value || ''}
                        onChange={handleChange}
                        className={`${styles.select} ${error ? styles.inputError : ''}`}
                    >
                        <option value="">
                            {placeholder || `Select ${label}`}
                        </option>
                        {options?.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                );

            case 'radio':
                return (
                    <div className={styles.radioGroup}>
                        {options?.map((option) => (
                            <label key={option.value} className={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name={name}
                                    value={option.value}
                                    checked={value === option.value}
                                    onChange={handleChange}
                                    disabled={disabled}
                                    required={required}
                                    className={styles.radioInput}
                                />
                                <span className={styles.radioText}>{option.label}</span>
                            </label>
                        ))}
                    </div>
                );

            case 'checkbox':
                return (
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            name={name}
                            checked={value === true || value === 'true'}
                            onChange={handleChange}
                            disabled={disabled}
                            className={styles.checkboxInput}
                        />
                        <span className={styles.checkboxText}>{label}</span>
                    </label>
                );

            case 'file':
                return (
                    <div className={styles.fileInputWrapper}>
                        <input
                            {...commonProps}
                            type="file"
                            onChange={handleChange}
                            accept={accept}
                            className={`${styles.fileInput} ${error ? styles.inputError : ''}`}
                        />
                        {value && value.length > 0 && (
                            <div className={styles.fileInfo}>
                                {Array.from(value).map((file, idx) => (
                                    <div key={idx} className={styles.fileName}>
                                        {file.name} ({(file.size / 1024).toFixed(2)} KB)
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            default:
                return (
                    <input
                        {...commonProps}
                        type="text"
                        value={value || ''}
                        onChange={handleChange}
                        placeholder={placeholder}
                        className={`${styles.input} ${error ? styles.inputError : ''}`}
                    />
                );
        }
    };

    return (
        <div className={styles.formField}>
            {type !== 'checkbox' && (
                <label htmlFor={name} className={styles.label}>
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}
            
            {renderInput()}
            
            {helpText && !error && (
                <div className={styles.helpText}>{helpText}</div>
            )}
            
            {error && (
                <div className={styles.error}>{error}</div>
            )}
        </div>
    );
};

FormField.propTypes = {
    field: PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        placeholder: PropTypes.string,
        required: PropTypes.bool,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.string.isRequired,
                label: PropTypes.string.isRequired
            })
        ),
        rows: PropTypes.number,
        helpText: PropTypes.string,
        accept: PropTypes.string,
        validation: PropTypes.object
    }).isRequired,
    value: PropTypes.any,
    error: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    disabled: PropTypes.bool
};

FormField.defaultProps = {
    value: '',
    error: null,
    onBlur: null,
    disabled: false
};

export default FormField;
